// src/components/tour/tours/monitoring.jsx
//
// Monitoring page tour (/monitoring/:serial). Shared by internal admins
// and client users, desktop and mobile. ctx is registered in MainPage.js
// with the RAW report; derived numbers use the same t247Shared helpers
// the layouts use, so the tour quotes exactly what's on screen.

import React from 'react';
import { replayStep } from './common';
import {
    buildDeviceData,
    computeWanRows,
    computeVlanRows,
    computePingStats,
    extractLogText,
    safeArr,
    fmt1,
    fmt2,
} from '../../monitoring/t247Shared';
import { canChangePriorityForType, reportPriorityLabel } from '../../../utils/wanPriority';

const chipClassFor = (ledColor) =>
    ledColor === 'red' ? 'chip-down' : ledColor === 'green' ? 'chip-up' : 'chip-other';

export function getMonitoringSteps(ctx, env) {
    const {
        deviceData: raw,
        serialNumber,
        period = 'daily',
        selectedDate,
        alerts,
        vlanAlerts,
        wanCapAlerts,
        starlinkAlerts,
        shouldSkipStarlink,
        managementType,
    } = ctx;
    const { isMobile, scope, role } = env;
    const isClient = scope === 'client';

    const d = buildDeviceData(raw);
    const name = d?.deviceName || serialNumber || 'this device';
    const isOnline = d?.lastStatus === 'online';
    const { rows: wanRows, totals } = computeWanRows(d);
    const vlanRows = computeVlanRows(d);
    const ping = computePingStats(d);
    const firstWan = wanRows[0];

    const cellularGB = wanRows.filter((r) => r.isCellular).reduce((s, r) => s + r.total, 0);
    const cellularPct = totals.total > 0 ? (cellularGB / totals.total) * 100 : null;
    const failovers = safeArr(d?.wanLogs).filter((l) => /fail|down/i.test(extractLogText(l))).length;

    const topVlan = [...vlanRows].sort((a, b) => (b.total || 0) - (a.total || 0))[0];
    const topClient = [...safeArr(d?.clientUsages)].sort((a, b) => (b.total_usage || 0) - (a.total_usage || 0))[0];
    const clientCount = safeArr(d?.clientUsages).length;

    const todayISO = new Date().toISOString().split('T')[0];
    const isLiveView = period === 'daily' && selectedDate === todayISO;
    const canChangePriority = role === 'admin' && isLiveView && canChangePriorityForType(managementType);

    const periodText = { daily: 'one day', weekly: '7 days', monthly: '30 days' }[period] || 'one day';
    const alertTotal = safeArr(alerts).length + safeArr(vlanAlerts).length + safeArr(wanCapAlerts).length +
        (shouldSkipStarlink ? 0 : safeArr(starlinkAlerts).length);

    const liveBadge = (
        <span className={`t247-live-badge ${isOnline ? '' : 'is-off'}`}>
            <span className="t247-dot" />
            {isOnline ? 'ONLINE' : 'OFFLINE'}
        </span>
    );

    const S = {
        intro: {
            target: null,
            title: `Monitoring ${name}`,
            content: (
                <>
                    <p>
                        This page is the full report for one router. Right now it's {liveBadge}
                    </p>
                    <p>We'll go through each section using this device's real data. Nothing is changed during the tour.</p>
                </>
            ),
        },

        device: {
            target: '[data-tour="mon-device"]',
            title: 'Which device you\'re looking at',
            content: (
                <p>
                    Name and serial <span className="pt-data">{serialNumber}</span>
                    {d?.model && !isMobile && <>, model <span className="pt-data">{d.model}</span></>}.
                    The badge shows whether it's online right now{!isMobile && ', and "Last updated" when this report was loaded'}.
                </p>
            ),
        },

        period: {
            target: '[data-tour="mon-period"]',
            title: 'Daily, weekly or monthly',
            content: (
                <>
                    <p>
                        Choose how much time the report covers. You're on{' '}
                        <span className="pt-data">{period.toUpperCase()}</span> ({periodText}).
                    </p>
                    <p>
                        <b>Daily</b> shows hourly detail plus live features (real-time bandwidth, SpeedFusion status).{' '}
                        <b>Weekly / Monthly</b> add up the last 7 or 30 days.
                    </p>
                </>
            ),
        },

        date: {
            target: '[data-tour="mon-date"]',
            title: 'Pick a date',
            content: (
                <p>
                    The day to report on. For weekly and monthly, it's the <b>last day</b> of the window.
                    {isLiveView && <> You're looking at <b>today</b>.</>}
                </p>
            ),
        },

        pdf: {
            target: '[data-tour="mon-pdf"]',
            title: 'Download as PDF',
            content: <p>Exports this exact report (same period and date) as a PDF to share or archive. It becomes available once the report has fully loaded.</p>,
        },

        // Desktop: both panels sit in one column -> one step.
        overview: {
            layout: 'desktop',
            target: '[data-tour="mon-overview"]',
            title: 'Device state & at a glance',
            content: (
                <>
                    <p><b>Device state</b>: uptime, connected clients, last online/offline times, model and firmware.</p>
                    {d && (
                        <p>
                            <b>At a glance</b>: <span className="pt-data">{fmt2(totals.total)} GB</span> went through the device
                            {cellularPct != null && <>, <span className="pt-data">{fmt1(cellularPct)}%</span> of it over cellular</>}
                            , with <span className="pt-data">{failovers}</span> failover event{failovers === 1 ? '' : 's'}.
                        </p>
                    )}
                </>
            ),
        },
        // Mobile: two separate panels -> two steps.
        stateMobile: {
            layout: 'mobile',
            target: '[data-tour="mon-state"]',
            title: 'Device state',
            content: <p>Uptime, number of connected clients, model and firmware version.</p>,
        },
        glanceMobile: {
            layout: 'mobile',
            target: '[data-tour="mon-glance"]',
            title: 'At a glance',
            content: d ? (
                <p>
                    <span className="pt-data">{fmt2(totals.total)} GB</span> went through the device
                    {cellularPct != null && <>, <span className="pt-data">{fmt1(cellularPct)}%</span> over cellular</>}
                    , with <span className="pt-data">{failovers}</span> failover event{failovers === 1 ? '' : 's'}.
                </p>
            ) : null,
        },

        wan: {
            target: '[data-tour="mon-wan"]',
            title: 'WAN status & usage',
            content: (
                <p>
                    Every internet link on this router (<span className="pt-data">{wanRows.length}</span> here):
                    its status, how much data it carried and its share of the traffic.{' '}
                    {isMobile ? 'Tap a card' : 'Click a row'} for full detail: IP and gateway,
                    plus signal and carrier for cellular, or latency and obstruction for Starlink.
                </p>
            ),
        },

        firstWan: {
            target: '[data-tour="mon-first-wan"]',
            title: firstWan ? `“${firstWan.name}”` : 'A WAN link',
            content: firstWan ? (
                <>
                    <p>
                        Status{' '}
                        <span className={`t247-chip ${chipClassFor(firstWan.ledColor)}`} style={{ textTransform: 'uppercase' }}>
                            {firstWan.statusLabel}
                        </span>{' '}
                        and <span className="pt-data">{fmt1(firstWan.pct)}%</span> of all traffic
                        (<span className="pt-data">{fmt2(firstWan.total)} GB</span>).
                    </p>
                    <p>
                        <span className={`t247-chip ${chipClassFor(firstWan.ledColor)}`}>
                            {reportPriorityLabel(firstWan.priority)}
                        </span>{' '}
                        is its priority: the router uses P1 first and falls back to P2, P3… if it goes down.
                        {!canChangePriority && ' Shown for information here.'}
                    </p>
                </>
            ) : null,
        },

        priority: {
            when: () => canChangePriority,
            target: '[data-tour="mon-first-prio"]',
            title: 'Change the priority',
            content: (
                <p>
                    {isMobile ? 'Tap' : 'Click'} the <b>P▾</b> chip to choose which link the router should prefer.
                    This is only possible on <b>today's daily</b> view; past dates and weekly/monthly are read-only history.
                </p>
            ),
        },

        distribution: {
            target: '[data-tour="mon-wan-dist"]',
            title: 'WAN usage distribution',
            content: <p>How download and upload were split between the links, so you can see at a glance which one carried the load.</p>,
        },

        vlan: {
            target: '[data-tour="mon-vlan"]',
            title: 'VLAN usage',
            content: (
                <p>
                    Data used per network segment
                    {topVlan && <> (the busiest is <span className="pt-data">{topVlan.vlan} · {topVlan.name}</span> with <span className="pt-data">{fmt2(topVlan.total)} GB</span>)</>}.{' '}
                    {isMobile ? 'Tap' : 'Click'} a VLAN to see which clients used it.
                </p>
            ),
        },

        graph: {
            target: '[data-tour="mon-graph"]',
            title: 'Data usage graph',
            content: (
                <p>
                    {period === 'daily'
                        ? 'Hour by hour usage for the selected day, per WAN link.'
                        : `Usage for each day of the ${periodText}, so you can spot unusual days.`}
                </p>
            ),
        },

        clients: {
            target: '[data-tour="mon-clients"]',
            title: 'Client usage',
            content: (
                <p>
                    Every device connected through this router (<span className="pt-data">{clientCount}</span>),
                    biggest users first
                    {topClient && <>. Top one: <span className="pt-data">{topClient.name}</span> with <span className="pt-data">{fmt2((topClient.total_usage || 0) / 1024)} GB</span></>}.
                    {!isMobile && <> Click <b>SORT</b> to flip the order.</>}
                </p>
            ),
        },

        logs: {
            target: '[data-tour="mon-logs"]',
            title: 'Logs & alerts',
            content: (
                <>
                    <p>
                        The router's own logs (PepVPN, WAN, System, DHCP{!isClient && ', Admin'}) and the alerts
                        we raised{alertTotal > 0 && <>: <span className="pt-data">{alertTotal}</span> for this period</>}.
                        <b> Cap</b> alerts mean a VLAN or WAN reached its data limit
                        {!isClient && <>, and <b>Starlink</b> shows plan overage</>}.
                    </p>
                    <p>Only tabs that have entries are shown. Times are in the router's local timezone.</p>
                </>
            ),
        },

        realtime: {
            when: () => period === 'daily',
            target: '[data-tour="mon-realtime"]',
            title: 'Real-time bandwidth',
            content: <p>Live traffic right now, refreshed automatically. Only shown on the daily view.</p>,
        },

        map: {
            target: '[data-tour="mon-map"]',
            title: 'Location tracking',
            content: <p>Where the router has been, from its GPS positions during this period.</p>,
        },

        replay: replayStep,
    };

    // Each layout has its own section order; follow what's on screen.
    const order = isMobile
        ? ['intro', 'device', 'period', 'date', 'pdf', 'stateMobile', 'glanceMobile', 'wan', 'firstWan', 'priority',
            'graph', 'vlan', 'clients', 'distribution', 'ping', 'realtime', 'map', 'logs', 'replay']
        : ['intro', 'device', 'period', 'date', 'pdf', 'overview', 'wan', 'firstWan', 'priority',
            'distribution', 'vlan', 'graph', 'clients', 'logs', 'ping', 'realtime', 'map', 'replay'];

    return order.map((k) => S[k]);
}
